import { RentSelectionLayout } from '@/lib/entities/page/model/rent-selection';

import { staticResource } from '@/lib/shared/utils/resources';
import { toMoney } from '@/lib/shared/utils/to-money';

import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';

import styles from './RentSelectionCard.module.scss';
import { getRentSelectionCardView } from './getRentSelectionCardView';
import {
  INCLUDED_SERVICE_ICONS,
  PAID_SERVICE_ICONS,
} from './rent-selection-services';

import { Button } from '../../Buttons';
import { H3, P2, P3 } from '../../Typography';

const PLACEHOLDER = '/images/placeholder_image.webp';

type RentSelectionCardPdfProps = {
  layout: RentSelectionLayout;
};

export const RentSelectionCardPdf = ({ layout }: RentSelectionCardPdfProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('index');
  const view = getRentSelectionCardView(layout, t, language, '$', 'USD', {});
  const coverSrc = staticResource(view.coverImage) || PLACEHOLDER;

  return (
    <li className={clsx(styles.card, styles.pdf)}>
      <div className={styles.gallery}>
        <div className={styles.gallery_wrapper}>
          <div className={styles.gallery_slider}>
            <Image
              sizes="1800px"
              className={styles.pdfImage}
              src={coverSrc}
              style={{ objectFit: 'cover', objectPosition: 'center' }}
              width={437}
              height={385}
              loading="eager"
              alt=""
            />
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <H3 className={styles.cardTitle}>{layout.name}</H3>
        <div className={styles.info}>
          <div className={styles.infoColumn}>
            <div className={styles.infoItem}>
              <P3>{t('location')}:</P3>
              <P2>{view.location}</P2>
            </div>
            {view.beachText ? (
              <div className={styles.infoItem}>
                <P3>{t('beach')}:</P3>
                <P2>{view.beachText}</P2>
              </div>
            ) : null}
            {view.bookingDates ? (
              <div className={styles.infoItem}>
                <P3>{t('rentSelectionPage.booking')}:</P3>
                <P2>{view.bookingDates}</P2>
              </div>
            ) : null}
          </div>
          <div className={styles.infoColumn}>
            {view.viewsText ? (
              <div className={styles.infoItem}>
                <P3>{t('view')}:</P3>
                <P2>{view.viewsText}</P2>
              </div>
            ) : null}
            <div className={styles.infoItem}>
              <P3>{t('bedrooms')}:</P3>
              <P2>{layout.bedrooms === 0 ? t('studio') : layout.bedrooms}</P2>
            </div>
            {layout.area ? (
              <div className={styles.infoItem}>
                <P3>{t('totalArea')}:</P3>
                <P2>
                  {layout.area} {t('prepositions.m2')}
                </P2>
              </div>
            ) : null}
          </div>
        </div>
        {view.includedServices.length > 0 ? (
          <div className={styles.services}>
            <P2>{t('rentSelectionPage.included')}</P2>
            <ul className={styles.chips}>
              {view.includedServices.map((key) => (
                <li key={key} className={styles.chip}>
                  {INCLUDED_SERVICE_ICONS[key] ? (
                    <span className={styles.chipIcon} aria-hidden>
                      {INCLUDED_SERVICE_ICONS[key]}
                    </span>
                  ) : null}
                  <span className={styles.chipLabel}>
                    {t(`rentSelectionPage.includedServices.${key}`, {
                      defaultValue: key,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {view.paidServices.length > 0 ? (
          <div className={styles.services}>
            <P2>{t('rentSelectionPage.paid')}</P2>
            <ul className={styles.chips}>
              {view.paidServices.map((item) => (
                <li key={item.key} className={styles.chip}>
                  {PAID_SERVICE_ICONS[item.key] ? (
                    <span className={styles.chipIcon} aria-hidden>
                      {PAID_SERVICE_ICONS[item.key]}
                    </span>
                  ) : null}
                  <span className={styles.chipLabel}>
                    {t(`rentSelectionPage.paidServices.${item.key}`, {
                      defaultValue: t(
                        `rentSelectionPage.includedServices.${item.key}`,
                        { defaultValue: item.key }
                      ),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {view.commentText ? (
          <div className={styles.comment}>
            <P2 className={styles.commentTitle}>
              {t('rentSelectionPage.comment')}
            </P2>
            <P3 className={styles.commentText}>{view.commentText}</P3>
          </div>
        ) : null}
        <div className={styles.bottom}>
          <div className={styles.prices}>
            {view.nightLabel ? (
              <H3 className={styles.price}>
                {t('rentSelectionPage.priceNight', { price: view.nightLabel })}
              </H3>
            ) : null}
            {view.totalValue != null && layout.nights ? (
              <H3 className={styles.price}>
                {t('rentSelectionPage.totalNights', {
                  count: layout.nights,
                  nightsWord: view.nightsWord,
                  price: `${view.totalSymbol} ${toMoney(view.totalValue) ?? view.totalValue}`,
                })}
              </H3>
            ) : view.totalValue != null ? (
              <H3 className={styles.price}>
                {t('rentSelectionPage.total', {
                  price: `${view.totalSymbol} ${toMoney(view.totalValue) ?? view.totalValue}`,
                })}
              </H3>
            ) : null}
          </div>
          {view.objectHref ? (
            <Button
              as={Link}
              href={view.objectHref}
              target="_blank"
              label={t('moreAbout')}
              theme={'dark'}
              size={'md'}
              scroll
            />
          ) : null}
        </div>
        <P3 className={styles.currencyDisclaimer}>
          {t('rentSelectionPage.currencyDisclaimer')}
        </P3>
      </div>
    </li>
  );
};
